import { isPersonIncludedInAdminDefinedChannel } from '@weavix/domain/src/channel/adc';
import {
    computed,
    getAccountStorage,
    idProp,
    Model,
    model,
    modelAction,
    prop,
} from '@weavix/mobx';
import { sortAlphabetical } from '@weavix/utils/src/sort';
import { facilityPeopleContext } from '../facility-people/facility-people-store';
import { myProfileContext, myUserId } from '../my-profile-store/my-profile-store';
import { peopleContext, Person } from '../people-store/people-store';
import { myUser, User, userRef, usersContext } from '../users-store/users-store';
import { ChannelMessage } from './channel-message';
import { ServiceLocator } from 'weavix-shared/services/service-locator';
import { AccessMap, ArchivedMap, ChannelConditionalOverride, ChannelDefaultFavorite, ChannelImageUrl, ChannelTask, ChannelType, ChannelTyper } from '@weavix/models/src/channel/channel';
import { TranslationService } from 'weavix-shared/services/translation.service';
import { ChannelNotifications } from '@weavix/models/src/channel/channel-notifications';
import { ComplexPersonCondition } from '@weavix/models/src/person/person-matcher';
import { observable, ObservableMap } from 'mobx';

const getMessages = () => getAccountStorage('channel-messages').Instance().index;
const userCache = new ObservableMap<string, User[]>();
export const userChannelCache = new ObservableMap<string, Set<string>>();
export const accountCache = new ObservableMap<string, Person[]>();
const nameCache = new ObservableMap<string, string>();
const adcFields = new Set([
    'facilityIds',
    'conditions',
]);

@model('Channel')
export class Channel extends Model({
    id: idProp,
    type: prop<ChannelType>(),
    lastMessage: prop<ChannelMessage>(),
    created: prop<string>(),
    updated: prop<string>(),
    name: prop<string>(),
    sequence: prop<number>(0),
    readSequence: prop<number>(0),
    readSequences: prop<Record<string, number>>(),
    people: prop<string[]>([]),
    facilityIds: prop<string[]>(),
    allPeople: prop<string[]>([]),
    pendingPeople: prop<string[]>(),
    creatorId: prop<string>(),
    notificationPreference: prop<ChannelNotifications>(),
    typers: prop<ChannelTyper[]>(() => []),
    accountId: prop<string>(),
    isReadOnly: prop<boolean>(),
    liveTranscription: prop<boolean>(),
    readOnlySenders: prop<string[]>(),
    favoriteIndex: prop<number>(),
    radioChannel: prop<number>(),
    isSnoozed: prop<boolean>(),
    conditionalNotificationOverrides: prop<ChannelConditionalOverride[]>(),
    defaultFavorite: prop<ChannelDefaultFavorite | null | undefined>(),
    task: prop<ChannelTask>(),

    conditions: prop<ComplexPersonCondition>(),

    /**
     * The below fields have to do with channelType === ChannelType.AdminApi
     * channelImageUrl is a URL to an image that represents the channel.
     */
    channelImageUrl: prop<ChannelImageUrl>(),

    /**
     * - ChannelType.People: ArchivedMap
     * - ChannelType.Channel: Date (passed as a string)
     */
    archived: prop<ArchivedMap | string>(),
    /**
     * Due to issues with the sync layer, the `access` property can't be synced like others.
     * So we fetch it on demand (see `channel.component.ts`).
     * Since this is related to Data Retention rules which don't run often,
     * we're OK with this value not being reactive.
     */
    access: prop<AccessMap>(),
    /**
     * The color for the Channel, used by the Speaker Mic to show a color on the LED Ring.
     */
    color: prop<string | undefined>(undefined),
}) {
    temporary?: boolean;

    // Used to force a reload of the users list
    // This is important for when a user changes their name or profile picture to display correctly on the channels screen
    @observable forceReloadUsers = 0;
    userReloadCount = 0;

    update(obj) {
        if (this.type === ChannelType.People && 'people' in obj) {
            const currentUsers = userCache.get(this.id);
            currentUsers?.forEach(u => this.removeUserChannelCache(u.id));
            userCache.delete(this.id);
            nameCache.delete(this.id);
        }
        if (this.type === ChannelType.Channel && Object.keys(obj).some(x => adcFields.has(x))) {
            userCache.delete(this.id);
        }
        // Fixing badge flicker on race condition on mobx events
        if (obj.lastMessage?.sequence && obj.lastMessage.senderId === myUserId()) obj.readSequence = obj.lastMessage.sequence;
        // if we locally overrode something, assume it will be up-to-date soon in the backend
        obj.readSequences = { ...this.readSequences, ...obj.readSequences };
        obj.notificationPreference = { ...this.notificationPreference, ...obj.notificationPreference };
        super.update(obj);
    }

    get readOnly() {
        return this.isReadOnly && !this.readOnlySenders?.includes(myProfileContext.get(this).userId)
            || this.radioChannel
            || this.isReadonlyDueToInactiveUsers
            || this.isReadOnly && this.type === ChannelType.SupportTicket;
    }

    @computed
    get isReadonlyDueToInactiveUsers() {
        if (this.type !== ChannelType.People) return false;
        const others = this.getOtherUsersForDependency();
        return others.length > 0 && others.every(u => u.inactiveUser);
    }

    // helps mobx track dependencies of users in computed properties
    private getOtherUsersForDependency() {
        const othersFromUsers = (this.users || []).filter(u => u.id !== myUserId());
        return othersFromUsers
            .filter(user => user.id !== myProfileContext.get(this).userId)
            .map(u => usersContext.get(this).get(u.id) || u);
    }

    get isNotebook() {
        // Notebook could also be a 1-user People channel.
        // TODO(post-5.19): clean up.
        return (this.type === ChannelType.People || this.type === ChannelType.Notebook)
            && this.people.includes(myUserId())
            && this.allPeople.length <= 1
            && !this.temporary;
    }

    @computed
    get displayName() {
        if (this.name) return this.name;
        let cached = nameCache.get(this.id);
        if (cached) return cached;
        const others = this.getOtherUsersForDependency();

        if (others.length === 0) {
            if (this.isNotebook) {
                return `${myUser().fullName ?? `${myUser().firstName} ${myUser().lastName}`} (${ServiceLocator.get(TranslationService).getImmediate('generics.me')})`;
            } else if (this.temporary) {
                return ServiceLocator.get(TranslationService).getImmediate('crews.channels.channel-details');
            } else {
                return ServiceLocator.get(TranslationService).getImmediate('generics.unknown-user');
            }
        } else {
            if (this.isTwoUsersInGroupChannel) {
                const otherPerson = this.people.find(person => person !== myUserId());
                const otherUser = userRef(otherPerson).current;
                return otherUser?.fullName ?? '';
            }
            const names = others.slice(0, 3)
                .map(user => user.fullName ?? `${user.firstName} ${user.lastName}`)
                .join(', ');
            const remaining = others.length - 3;
            const count = remaining > 0 ? ` +${remaining}` : '';
            cached = `${names}${count}`;
        }
        // if temporary, don't cache name as some may be added
        if (this.temporary) return cached;
        else {
            nameCache.set(this.id, cached);
            return cached;
        }
    }

    @modelAction
    removeUserChannelCache(userId: string) {
        userChannelCache.get(userId)?.delete(this.id);
    }

    @modelAction
    addUserChannelCache(userId: string) {
        const existing = userChannelCache.get(userId);
        if (existing) {
            existing.add(this.id);
        } else if (!existing) {
            userChannelCache.set(userId, new Set([this.id]));
        }
    }

    @computed
    get users() {
        const cached = userCache.get(this.id);
        if (cached && this.forceReloadUsers === this.userReloadCount) return cached;
        this.userReloadCount = this.forceReloadUsers;

        if (this.type === ChannelType.People) {
            // if temporary, don't cache users as some may be added
            if (this.temporary) {
                return this.people.map(p => userRef(p).maybeCurrent);
            } else {
                this.people.forEach(p => this.addUserChannelCache(p));
                userCache.set(this.id, sortAlphabetical(this.people.map(p => usersContext.get().getStatic(p)).filter(Boolean), 'lastName'));
                return userCache.get(this.id);
            }
        }

        const isPersonOnFacility = (facilityId: string, person: Person) => !!facilityPeopleContext.get().getPerson(person.id, facilityId);

        let allPeople = accountCache.get(this.accountId);
        if (!allPeople) {
            accountCache.set(this.accountId, peopleContext.get().getAllStatic({ accountId: this.accountId }).filter(x => x.hasUser));
            allPeople = accountCache.get(this.accountId);
        }

        const includedPeople = allPeople.filter(p => isPersonIncludedInAdminDefinedChannel(p, this, isPersonOnFacility));
        userCache.set(this.id, sortAlphabetical(includedPeople.map(p => usersContext.get().getStatic(p.id)).filter(Boolean), 'lastName'));
        return userCache.get(this.id);
    }

    @computed
    get pendingUsers() {
        return this.pendingPeople.map(x => userRef(x).maybeCurrent).filter(Boolean) as User[];
    }

    get usersWithPendingUsers() {
        return this.users.concat(this.pendingUsers) as User[];
    }

    @computed
    get otherUsers() {
        return this.users?.filter(x => x.id !== myProfileContext.get(this).userId);
    }

    get sortedOtherUserIds(): string[] {
        return this.otherUsers.map(x => x.id).sort();
    }

    @computed
    get creator() {
        return userRef(this.creatorId).maybeCurrent;
    }

    @computed
    get userNamesTitle(): string {
        const users = this.getOtherUsersForDependency();
        return users.length === 1 ? users[0].fullName : users.map(x => x.firstName).join(', ');
    }

    @computed
    get pendingUserNamesTitle(): string {
        const users = this.pendingUsers;
        return users.length === 1 ? users[0].fullName : users.map(x => x.firstName).join(', ');
    }

    @computed
    get isTwoUsersInGroupChannel(): boolean {
        return this.type === ChannelType.People && this.allPeople.length > 2 && this.people.length === 2;
    }

    @computed
    get fullTitle(): string {
        return this.name?.length ? this.name : this.userNamesTitle;
    }

    @computed
    get searchText() {
        return `${this.name} ${this.users.map(x => x.searchText).join(' ')}`?.toLowerCase();
    }

    @computed
    get unreadCount() {
        return Math.max(0, this.sequence - this.readSequence);
    }

    @computed
    get channelLanguages(): string[] {
        const languages = [];
        this.users.forEach(x => {
            if (x.locale && !languages.includes(x.locale)) languages.push(x.locale);
        });
        return languages;
    }

    @computed
    get isAiVoiceAssistantChannel(): boolean {
        return this.type === ChannelType.AiVoiceAssistant;
    }

    removePending(pending: ChannelMessage) {
        getMessages().remove(pending.id);
    }

    @modelAction
    setReadSequence(readSequence: number) {
        this.readSequence = readSequence;
    }

    @modelAction
    clearUserCache() {
        userCache.delete(this.id);
        this.forceReloadUsers++;
    }

    @modelAction
    invalidateUserCache() {
        userCache.delete(this.id);
        nameCache.delete(this.id);
        this.forceReloadUsers++;
    }
}
